import { useEffect, useRef, useState } from "react";

import { useDropdownItemsCtxProvider } from "@ryde/redux";
import {
  getDropdownOptionLabel,
  IDropdownItem,
  TDropdownItemsKeeperIndex,
} from "@ryde/shared";

const buildSiteLabelsMap = (
  siteIds: number[],
  dropdownItems: IDropdownItem[],
): Map<number, string> => {
  const dropdownItemsKeeperIndex: TDropdownItemsKeeperIndex = new Map();
  const labelsMap = new Map<number, string>();

  for (const siteId of siteIds) {
    const label = getDropdownOptionLabel({
      entity: "site",
      dropdownList: dropdownItems,
      id: siteId,
      dropdownItemsKeeperIndex,
    });
    labelsMap.set(siteId, label || String(siteId));
  }

  return labelsMap;
};

const areSiteLabelMapsEqual = (
  left: Map<number, string>,
  right: Map<number, string>,
): boolean => {
  if (left.size !== right.size) {
    return false;
  }

  for (const [siteId, label] of left) {
    if (right.get(siteId) !== label) {
      return false;
    }
  }

  return true;
};

export const useRideBottomSiteLabels = (
  siteIdsKey: string,
): Map<number, string> => {
  const { getDropdownItems } = useDropdownItemsCtxProvider();
  const getDropdownItemsRef = useRef(getDropdownItems);
  getDropdownItemsRef.current = getDropdownItems;

  const [siteLabelsById, setSiteLabelsById] = useState<Map<number, string>>(
    () => new Map(),
  );

  useEffect(() => {
    if (!siteIdsKey) {
      setSiteLabelsById(prev => (prev.size === 0 ? prev : new Map()));
      return;
    }

    const siteIds = siteIdsKey.split(",").map(Number);
    let cancelled = false;

    const loadSiteLabels = async (): Promise<void> => {
      const dropdownItems = await getDropdownItemsRef.current("site");
      if (cancelled) {
        return;
      }

      const labelsMap = buildSiteLabelsMap(siteIds, dropdownItems);
      setSiteLabelsById(prev =>
        areSiteLabelMapsEqual(prev, labelsMap) ? prev : labelsMap,
      );
    };

    void loadSiteLabels();

    return () => {
      cancelled = true;
    };
  }, [siteIdsKey]);

  return siteLabelsById;
};
